// src/pages/AdminReportManagement/AdminReportManagement.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faDollarSign,
  faMoneyBillWave, // [CASH-MODE]
  faHandHoldingDollar
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the Admin Report Management options
const adminReportOptions = [
  {
    title: 'SUPER ADMIN Transaction Report',
    icon: faExchangeAlt,
    path: '/admin-report/transactions',
    perm: 'reports.adminTransactions',
    description: 'View detailed transaction breakdown with commission splitups'
  },
  {
    title: 'SUPER ADMIN Daily Report',
    icon: faDollarSign,
    path: '/admin-report/commissions',
    perm: 'reports.adminDaily',
    description: 'Track daily aggregated commissions and revenue splitups'
  },
  {
    title: 'POOL CASHBACK ACCOUNT',
    icon: faExchangeAlt,
    path: '/admin-report/pool-cashback-account',
    perm: 'reports.pool',
    description: 'View current pool cashback balance and transaction history'
  },
  // [CASH-MODE]
  {
    title: 'Cash Transactions',
    icon: faMoneyBillWave,
    path: '/admin-report/cash-transactions',
    perm: 'reports.cash',
    description: 'Cash payments recorded by shops, with commission / game processing status'
  },
  {
    title: 'Non-Customer Commission',
    icon: faHandHoldingDollar,
    path: '/admin-report/non-customer-commission',
    perm: 'reports.nonCustomerCommission',
    description: 'Commission from payments made without a registered customer'
  }
];

const AdminReportManagement = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = adminReportOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access admin report features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        Admin Report Management
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {adminReportOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 225,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      style={{ color: 'orange', marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default AdminReportManagement;